import EditIcon from "@mui/icons-material/Edit";
import CableIcon from "@mui/icons-material/Cable";
import {
  alpha,
  Box,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import type { ScrapEyeSensorPlacementRead } from "src/store/api/generatedApi";
import { PositionBadge } from "./PositionBadge";
import type { TopologyEdit, TopologySelection } from "./topologyEditing";

interface SensorTopologyNodeProps {
  target: TopologySelection;
  name: string;
  subtitle: string;
  left: number;
  top: number;
  width: number;
  height: number;
  selected: boolean;
  active: boolean;
  busy: boolean;
  dragging: boolean;
  installations: ScrapEyeSensorPlacementRead[];
  positionColours: Map<number, string>;
  onSelect: (selection: TopologySelection) => void;
  onEdit: (edit: TopologyEdit) => void;
  onEditMetadata: (sensorId: number) => void;
  onDrop: (selection: TopologySelection) => void;
  onDragStart: (sensorId: number) => void;
  onDragEnd: () => void;
}

export const SensorTopologyNode = ({
  target,
  name,
  subtitle,
  left,
  top,
  width,
  height,
  selected,
  active,
  busy,
  dragging,
  installations,
  positionColours,
  onSelect,
  onEdit,
  onEditMetadata,
  onDrop,
  onDragStart,
  onDragEnd,
}: SensorTopologyNodeProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const colour =
    target.kind === "position" ? positionColours.get(target.id) : null;
  return (
    <Paper
      variant="outlined"
      onDragOver={(event) => {
        if (dragging && target.kind !== "sensor") event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(target);
      }}
      sx={{
        position: "absolute",
        left,
        top,
        width,
        height,
        borderColor: selected ? "primary.main" : "divider",
        borderWidth: selected ? 2 : 1,
        opacity: active ? 1 : 0.6,
        bgcolor: colour == null ? "background.paper" : alpha(colour, 0.08),
        ...(colour == null
          ? {}
          : { borderBottomWidth: 3, borderBottomColor: colour }),
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Stack direction="row" alignItems="center" sx={{ minHeight: 56 }}>
          <Button
            onClick={() => {
              onSelect(target);
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              justifyContent: "flex-start",
              textAlign: "left",
              height: "100%",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  fontWeight:
                    target.kind === "position"
                      ? "fontWeightBold"
                      : "fontWeightRegular",
                }}
                noWrap
              >
                {target.kind === "position" ? subtitle : name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {target.kind === "position" ? name : subtitle}
              </Typography>
            </Box>
          </Button>
          {target.kind === "sensor" && (
            <Tooltip title={t("edit")}>
              <IconButton
                size="small"
                aria-label={`${t("edit")} ${name}`}
                disabled={busy}
                onClick={() => {
                  onEditMetadata(target.id);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {target.kind === "sensor" && (
            <Button
              size="small"
              disabled={busy}
              draggable={!busy}
              aria-label={t("topologyConnectSensor", { name })}
              onDragStart={(event) => {
                event.dataTransfer.setData("text/plain", String(target.id));
                onDragStart(target.id);
              }}
              onDragEnd={onDragEnd}
              onClick={() => {
                onEdit({
                  kind: "connect",
                  sensorId: target.id,
                  deviceId: null,
                });
              }}
              sx={{ minWidth: 32 }}
            >
              <CableIcon fontSize="small" />
            </Button>
          )}
        </Stack>
        {installations.length > 0 && (
          <Stack spacing={0.25} sx={{ px: 1, pb: 0.5, overflowY: "auto" }}>
            {installations.map((installation) => (
              <PositionBadge
                key={installation.id}
                colour={
                  positionColours.get(installation.recording_position_id) ??
                  theme.palette.text.secondary
                }
                name={installation.recording_position_name}
                location={installation.location_name}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
